import {Box, Flex, InputLeftElement, useOutsideClick} from '@chakra-ui/react';
import SearchSvg from 'assets/SVG/SearchSvg';
import {colors} from 'components/providers';
import {InputSearchWithDebounce} from 'components/shared';
import {useDesktop, useTranslation} from 'hooks';
import {ArrowLeft} from 'lucide-react';
import {useRouter} from 'next/router';
import React, {FC, useEffect, useRef, useState} from 'react';
import {IProduct, IRestaurantResponse} from 'types';

import {IMetroSearchResponse} from '../../metro/types';
import {MetroSearchResults} from '../metro-search/components';

interface IProps {
  data?: IMetroSearchResponse;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  heightLG?: boolean;
  isLoading?: boolean;
  provider?: IRestaurantResponse;
  onProductClick?: (product: IProduct) => void;
}

export const MetroSearchInput: FC<IProps> = React.memo(
  ({data, placeholder, value, onChange, heightLG, isLoading, provider, onProductClick}) => {
    const {t} = useTranslation();
    const router = useRouter();
    const [showSuggestions, setShowSuggestions] = useState(false);
    const isDesktop = useDesktop();
    const isMobile = !isDesktop;
    const ref = useRef<any>();
    const lastSubmittedValue = useRef<string>('');
    const inputRef = useRef<HTMLInputElement>(null);

    useOutsideClick({
      ref,
      handler: (event: Event) => {
        const target = event.target as HTMLElement | null;
        if (target?.closest('[role="alertdialog"]')) {
          return;
        }
        if (target?.closest('[role="dialog"], .chakra-modal__content, .chakra-modal__content-container')) {
          return;
        }
        setShowSuggestions(false);
      },
    });

    const onClearSearchField = () => {
      onChange('');
      setShowSuggestions(false);
    };

    const handleInputChangeWithDebounce = (value: string) => {
      onChange(value);
      const shouldShow = value.length > 1 && value.trim() !== lastSubmittedValue.current;
      setShowSuggestions(shouldShow);
    };

    const handleContainerFocus = () => setShowSuggestions(value.length > 1);

    const handleSearch = (query: string) => {
      const current = query.trim();
      if (current.trim().length >= 2) {
        setShowSuggestions(false);
        router.push(`/market-category/search?query=${encodeURIComponent(current.trim())}`);
        lastSubmittedValue.current = current.trim();
        inputRef?.current?.blur();
      }
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      handleSearch(value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearch(e.currentTarget.value);
      }
    };

    const handleSearchIconClick = () => {
      const current = inputRef.current?.value;
      if (current) {
        handleSearch(current);
      }
    };

    const containerProps: any = {
      position: isMobile && showSuggestions ? 'fixed' : 'relative',
      zIndex: isMobile && showSuggestions ? 2001 : 9,
      top: isMobile && showSuggestions ? 0 : undefined,
      left: isMobile && showSuggestions ? 0 : undefined,
      width: isMobile && showSuggestions ? '100vw' : 'auto',
      bg: isMobile && showSuggestions ? colors.lightGray : undefined,
    };

    const originalOverflowRef = useRef<string>('');
    useEffect(() => {
      if (isMobile) {
        if (showSuggestions) {
          originalOverflowRef.current = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = originalOverflowRef.current || '';
        }
      }
      return () => {
        if (isMobile) {
          document.body.style.overflow = originalOverflowRef.current || '';
        }
      };
    }, [showSuggestions, isMobile]);

    return (
      <Box {...containerProps} ref={ref} onFocus={handleContainerFocus}>
        <form onSubmit={handleSubmit}>
          <InputSearchWithDebounce
            inputRef={inputRef}
            searchValue={value}
            placeholder={placeholder ?? t('SEARCH')}
            onChangeValueWithDebounce={handleInputChangeWithDebounce}
            onClearSearchField={onClearSearchField}
            onKeyDown={handleKeyDown}
            delay={500}
            cssPropsForInput={{
              borderRadius: showSuggestions && isMobile ? '0' : '1rem',
              borderColor: heightLG ? 'gray' : '#EBECF1',
              _focus: {border: showSuggestions && isMobile ? 'none' : '1px solid black', opacity: ''},
              _placeholder: {
                fontWeight: 600,
                color: colors.black,
              },
              pl: '56px',
              backgroundColor: showSuggestions && isMobile ? colors.lightGray : colors.white,
              color: colors.black,
              height: heightLG ? '78px' : '46px',
            }}
          >
            <InputLeftElement pointerEvents="auto" h="full">
              {isMobile && showSuggestions ? (
                <Flex ml="18px" alignItems="center" h="full" cursor="pointer" onClick={() => setShowSuggestions(false)}>
                  <ArrowLeft />
                </Flex>
              ) : (
                <Flex ml="18px" alignItems="center" h="full" cursor="pointer" onClick={handleSearchIconClick}>
                  <SearchSvg fill={colors.black} />
                </Flex>
              )}
            </InputLeftElement>
          </InputSearchWithDebounce>
        </form>
        {showSuggestions && (
          <Box>
            <MetroSearchResults
              value={value}
              data={data}
              onProductClick={onProductClick}
              topPadding={heightLG ? '78px' : '46px'}
              isLoading={isLoading}
              provider={provider}
              inputRef={inputRef}
              handleSearch={handleSubmit}
              setShowSuggestions={setShowSuggestions}
            />
          </Box>
        )}
      </Box>
    );
  },
);
